import * as React from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay } from '@fortawesome/free-solid-svg-icons/faPlay'
import { faPause } from '@fortawesome/free-solid-svg-icons/faPause'
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons/faRotateLeft'
import { useSnapshot } from 'valtio'
import { dayHistoryState } from '../../../../store/dayHistoryState.ts'
import { historyState } from '../../../../store/historyState.ts'
import { dataState } from '../../../../store/dataState.ts'
import { authState, effectiveTimezone } from '../../../../store/authState.ts'
import { cycleSpeed, formatSpeed } from '../../../../helpers/dayHistoryHelper.ts'

/**
 * Day History playback controls.
 * Shows a large "Play Day" overlay when first loaded, then
 * switches to compact transport controls once playback starts.
 */
export const DayHistoryControls: React.FC = () => {
    const isPlaying = (useSnapshot(dayHistoryState) as typeof dayHistoryState).isPlaying
    const playbackSpeed = (useSnapshot(dayHistoryState) as typeof dayHistoryState).playbackSpeed
    const currentTime = (useSnapshot(dayHistoryState) as typeof dayHistoryState).currentTime
    const pathData = (useSnapshot(dayHistoryState) as typeof dayHistoryState).pathData
    const loading = (useSnapshot(dayHistoryState) as typeof dayHistoryState).loading
    const error = (useSnapshot(dayHistoryState) as typeof dayHistoryState).error
    const selectedPetId = (useSnapshot(historyState) as typeof historyState).selectedPetId
    const pets = (useSnapshot(dataState) as typeof dataState).pets
    useSnapshot(authState)
    const zone = effectiveTimezone().value

    const [hasStarted, setHasStarted] = React.useState(false)

    // Reset hasStarted when path data changes (new day selected)
    React.useEffect(() => {
        setHasStarted(false)
    }, [pathData])

    if (!pathData && !loading && !error) return null

    const pet = pets.find(p => p._id === selectedPetId)

    const startPlayback = () => {
        const store = dayHistoryState
        if (!store.pathData) return
        store.currentTime = store.pathData.duration.start
        store.isPlaying = true
        setHasStarted(true)
    }

    const togglePlay = () => {
        const store = dayHistoryState
        if (!store.pathData) return

        if (!hasStarted) {
            startPlayback()
            return
        }

        // If at end, restart from beginning
        if (store.currentTime >= store.pathData.duration.end) {
            store.currentTime = store.pathData.duration.start
        }
        store.isPlaying = !store.isPlaying
    }

    const restart = () => {
        const store = dayHistoryState
        if (!store.pathData) return
        store.isPlaying = false
        store.currentTime = store.pathData.duration.start
    }

    const changeSpeed = () => {
        const next = cycleSpeed(playbackSpeed)
        dayHistoryState.playbackSpeed = next
    }

    // Format current simulated time in user's timezone
    const clockDisplay = currentTime
        ? DateTime.fromSeconds(currentTime).setZone(zone).toFormat('h:mm:ss a')
        : '--:--'

    // No data state
    if (pathData && pathData.path.length === 0) {
        return (
            <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg text-center'}>
                <div className={'text-primary font-semibold'}>No activity recorded for this day</div>
            </div>
        )
    }

    // Loading state
    if (loading) {
        return (
            <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg flex items-center justify-center gap-2'}>
                <span className="loading loading-spinner loading-sm text-primary"></span>
                <span className={'text-primary font-semibold'}>Loading path...</span>
            </div>
        )
    }

    // Error state
    if (error) {
        return (
            <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg text-center'}>
                <div className={'text-error font-semibold'}>{error}</div>
            </div>
        )
    }

    // Big play overlay — initial state before user has pressed play
    if (!hasStarted) {
        const legend = pathData?.gaps?.length ? (() => {
            const presentReasons = new Set(pathData.gaps.map(g => g.reason))
            const legendItems: { reason: string; label: string; color: string }[] = [
                { reason: 'active', label: 'Active', color: 'bg-primary' },
                { reason: 'sleeping', label: 'Sleeping', color: 'bg-indigo-400' },
                { reason: 'powered_off', label: 'Powered off', color: 'bg-gray-400' },
                { reason: 'charging', label: 'In charger', color: 'bg-emerald-400' },
                { reason: 'power_saving', label: 'Power saving', color: 'bg-violet-400' },
                { reason: 'invalid_gps', label: 'Weak GPS', color: 'bg-amber-400' },
                { reason: 'no_data', label: 'No reports', color: 'bg-rose-400' },
            ]
            const visible = legendItems.filter(item => item.reason === 'active' || presentReasons.has(item.reason as any))
            if (visible.length === 0) return null
            return (
                <div className={'flex flex-wrap gap-x-2 gap-y-0.5'}>
                    {visible.map(item => (
                        <div key={item.reason} className={'flex items-center gap-1'}>
                            <div className={`w-2 h-2 rounded-full ${item.color} opacity-70`} />
                            <span className={'text-[10px] text-base-content/60'}>{item.label}</span>
                        </div>
                    ))}
                </div>
            )
        })() : null

        return (
            <div className={'relative flex items-center py-2'}>
                {/* Legend — left justified */}
                {legend && (
                    <div className={'absolute left-0'}>
                        {legend}
                    </div>
                )}
                {/* Play button — centered */}
                <div className={'mx-auto'}>
                    <button
                        className={'btn btn-primary btn-sm gap-2 rounded-full px-5 shadow-lg'}
                        onClick={startPlayback}
                    >
                        <FontAwesomeIcon icon={faPlay} />
                        <span>Play {pet?.petName ? `${pet.petName}'s` : ''} Day</span>
                    </button>
                </div>
            </div>
        )
    }

    // Compact transport controls — shown after playback has started
    return (
        <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg flex items-center gap-3 w-fit mx-auto'}>
            {/* Restart button */}
            <button
                className={'btn btn-ghost btn-sm btn-circle text-primary'}
                onClick={restart}
                title={'Restart'}
            >
                <FontAwesomeIcon icon={faRotateLeft} />
            </button>

            {/* Play/Pause */}
            <button
                className={'btn btn-primary btn-sm btn-circle'}
                onClick={togglePlay}
                title={isPlaying ? 'Pause' : 'Play'}
            >
                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </button>

            {/* Speed toggle */}
            <div className={'flex items-center gap-1'}>
                <span className={'text-xs text-primary/60'}>Speed:</span>
                <button
                    className={'btn btn-ghost btn-xs text-primary font-bold min-w-[4.5rem] underline decoration-dotted'}
                    onClick={changeSpeed}
                    title={'Click to change playback speed'}
                >
                    {formatSpeed(playbackSpeed)}
                </button>
            </div>

            {/* Clock */}
            <div className={'text-primary font-mono text-sm font-semibold min-w-[5.5rem] text-center'}>
                {clockDisplay}
            </div>
        </div>
    )
}
